<template>
  <v-dialog
    v-model="this.isOpen"
    max-width="733"
    @click:outside="isOpen = false"
  >
    <v-card v-if="data">
      <v-card-title>{{ data.identifier }} - {{ data.name }}</v-card-title>
      <v-card-subtitle></v-card-subtitle>

      <v-card-text>
        <div v-if="data.pictograms.length">
          <div class="text-h6 mb-2">
            {{ $t("dangerousGoods.dangerousGoodsList.tableHeader.pictograms") }}
          </div>
          <v-row>
            <v-col
              v-for="pictogram of data.pictograms"
              :key="pictogram"
              cols="6"
              sm="3"
              lg="3"
            >
              <v-card outlined class="pictogram-card">
                <v-card-text>
                  <pictogram :image-path="pictogram" />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>
        <div v-if="hAndPSentences.length">
          <v-divider class="my-4" />
          <div class="text-h6 mb-2">
            {{
              $t("dangerousGoods.dangerousGoodsList.tableHeader.hAndPSentences")
            }}
          </div>
          <v-row no-gutters>
            <v-col
              v-for="sentence of hAndPSentences"
              :key="sentence.code"
              cols="12"
              class="d-flex align-center py-1"
            >
              <v-chip
                small
                label
                class="mr-2 font-weight-medium"
                :color="sentence.severity || undefined"
                :dark="sentence.severity === 'red'"
                :outlined="!sentence.severity"
              >
                {{ sentence.code }}
              </v-chip>
              <span>{{ sentence.text }}</span>
            </v-col>
          </v-row>
        </div>
        <div v-for="row of detailRows" :key="row.label">
          <v-divider class="my-4" />
          <div class="text-h6 mb-2"></div>
          <v-row no-gutters>
            <v-col cols="6" class="font-weight-bold">{{ row.label }}</v-col>
            <v-col cols="6" class="text-end">{{ row.value }}</v-col>
          </v-row>
        </div>
        <div v-if="data?.safetyDataSheet?.name">
          <v-divider class="my-4" />
          <v-row no-gutters align="center">
            <v-col cols="6" class="font-weight-bold">
              {{
                $t(
                  "dangerousGoods.dangerousGoodsList.tableHeader.safetyDataSheet"
                )
              }}
            </v-col>
            <v-col cols="6" class="text-end">
              <v-icon medium @click.stop="downloadItem(data)">
                picture_as_pdf
              </v-icon>
            </v-col>
          </v-row>
        </div>
        <div v-if="linkedDocumentLabel">
          <v-divider class="my-4" />
          <v-row no-gutters align="center">
            <v-col cols="6" class="font-weight-bold">
              {{
                $t(
                  "dangerousGoods.dangerousGoodsList.tableHeader.operatingInstruction"
                )
              }}
            </v-col>
            <v-col cols="6" class="text-end">
              <operatingInstructionCell :item="data" class="d-inline-block" />
            </v-col>
          </v-row>
        </div>
        <div v-if="additionalFiles.length">
          <v-divider class="my-4" />
          <v-row no-gutters align="center">
            <v-col cols="6" class="font-weight-bold">
              {{ $t("dangerousGoods.dangerousGoodsList.form.additionalFiles") }}
            </v-col>
            <v-col cols="6" class="text-end">
              <v-tooltip
                v-for="(file, index) in additionalFiles"
                :key="index"
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    medium
                    class="ml-2"
                    v-on="on"
                    @click.stop="downloadAdditionalFile(file)"
                  >
                    picture_as_pdf
                  </v-icon>
                </template>
                <span>{{ file.name }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </div>
        <div v-if="formattedSafetyDataSheetDate">
          <v-divider class="my-4" />
          <div class="text-h6 mb-2"></div>
          <v-row no-gutters>
            <v-col cols="6" class="font-weight-bold">
              {{
                $t("dangerousGoods.dangerousGoodsList.form.safetyDataSheetDate")
              }}
            </v-col>
            <v-col cols="6" class="text-end">
              {{ formattedSafetyDataSheetDate }}
            </v-col>
          </v-row>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text color="primary" v-on:click="isOpen = false">{{
          $t("general.back")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters } from "vuex";
import Pictogram from "@/components/layout/pictogram.vue";
import operatingInstructionCell from "./dangerousGoodsListOperatingInstruction.vue";
import { dateUtil } from "../../../api/api";
import { hazardSeverity } from "../../../../data/criticalHazardCodes";

export default {
  components: { Pictogram, operatingInstructionCell },
  data: () => ({
    isOpen: false,
    data: undefined,
  }),
  watch: {
    isOpen(newVal) {
      if (!newVal) {
        this.data = undefined;
        this.overdueAssignments = undefined;
        this.finishedAssignments = undefined;
      }
    },
  },
  computed: {
    ...mapGetters(["tableDataGetter", "hSentences", "pSentences"]),
    hAndPSentences() {
      const codes = this.data ? [...this.data.h, ...this.data.p] : [];
      const rank = { red: 0, yellow: 1, "": 2 };

      return codes
        .map((code) => {
          const full =
            this.allHAndPSentences.find((sentence) => sentence.value === code)
              ?.name ?? code;
          //split "H301: Giftig ..." into code + wording; the code column is
          //the one that gets coloured
          const separator = full.indexOf(":");
          return {
            code,
            text: separator >= 0 ? full.slice(separator + 1).trim() : "",
            severity: hazardSeverity(code),
          };
        })
        .sort((a, b) => rank[a.severity] - rank[b.severity]);
    },
    allHAndPSentences() {
      return [...this.hSentences, ...this.pSentences];
    },
    //plain label/value rows; empty ones are dropped
    detailRows() {
      if (!this.data) return [];

      const header = (key) =>
        this.$t(`dangerousGoods.dangerousGoodsList.tableHeader.${key}`);
      const yesNo = this.data.fss7Relevant
        ? this.$t("general.yes")
        : this.$t("general.no");

      return [
        {
          label: header("storageClassification"),
          value: this.data.storageClassification,
        },
        {
          label: header("waterHazardClass"),
          value: this.data.waterHazardClass,
        },
        { label: header("containerSize"), value: this.data.containerSize },
        { label: header("yearlyUsage"), value: this.data.yearlyUsage },
        { label: header("usageLocation"), value: this.data.usageLocation },
        { label: header("manufacturer"), value: this.data.manufacturer },
        {
          label: this.$t(
            "dangerousGoods.dangerousGoodsList.form.responsiblePerson"
          ),
          value: this.data.responsiblePerson,
        },
        {
          label: this.$t("dangerousGoods.dangerousGoodsList.form.fss7Relevant"),
          value: yesNo,
        },
      ].filter(
        (row) =>
          row.value !== null && row.value !== undefined && row.value !== ""
      );
    },
    linkedDocumentLabel() {
      const uploaded = this.data?.operatingInstruction;
      if (uploaded && typeof uploaded === "object") return uploaded.name;

      const generated = this.data?.betriebsanweisung;
      if (generated && typeof generated === "object") return generated.title;

      return "";
    },
    formattedSafetyDataSheetDate() {
      if (!this.data?.safetyDataSheetDate) return "";
      return dateUtil(new Date(this.data.safetyDataSheetDate), false, "");
    },
    additionalFiles() {
      return (this.data?.additionalFiles || []).filter(
        (file) => file?.location
      );
    },
  },
  methods: {
    openDetails(data) {
      this.data = data;
      this.isOpen = true;
    },
    async downloadAdditionalFile(file) {
      const path = `Gefahrstoff-Dokumente/${file.name}_${file.location}.${file.filetype}`;
      const url = await this.$store.dispatch("getPresignedDownloadUrl", path);
      if (url) window.open(url, "_blank");
    },
    async downloadItem(item) {
      //open url to download file
      window.open(await this.getDownloadItemLink(item), "_blank");
    },
    async getDownloadItemLink(item) {
      //download path
      const formPath = this.createFormPath(item);
      if (formPath) {
        return await this.$store.dispatch("getPresignedDownloadUrl", formPath);
      }
    },
    createFormPath(item) {
      if (item) {
        return (
          "Sicherheitsdatenblaetter/" +
          this.data?.safetyDataSheet.name +
          "_" +
          this.data?.safetyDataSheet.location +
          "." +
          this.data?.safetyDataSheet.filetype
        );
      }
    },
  },
};
</script>

<style scoped>
.pictogram-card {
  height: 100%;
}
</style>
